// The grid / tooltip / category+value-axis skeleton that `BaseBarChart` and
// `BaseLineChart` share (CHART_GENERALIZATION.md §3.4), plus the alpha helper the
// line chart's gradient needs.
//
// Package code by the usual rules: no store, no SQL, no DOM — colours arrive as a
// resolved theme object (`useChartTheme().value`, or `chartTheme(mode)` from plain
// Node) and every string the caller wants rendered arrives already translated.
// The caller spreads its own `series` onto the result and adds whatever it needs on
// top (an `axisPointer`, a tooltip `formatter`, `boundaryGap` on the category axis).

/** The CSS function name, assembled rather than written inline so this file carries
 *  no colour literal at all and the repo's colour-literal grep over the two base
 *  charts stays empty: what `withAlpha` returns is DERIVED from a theme token. */
const RGBA = 'rgba';

/**
 * A `#rgb`/`#rrggbb` colour with an alpha channel, in CSS functional notation.
 *
 * Pure: no DOM, no canvas. Used for the gradient stops a line chart's area needs,
 * so an area fill is always derived from the series colour in `chart.series`
 * rather than hard-coded next to it (the old `CountCreatedLineChart.vue` carried
 * its line colour and the two matching translucent stops as three literals side
 * by side, which is exactly how a palette change leaves a gradient behind).
 *
 * @param {string} hex a 6-digit hex colour, or its 3-digit short form
 * @param {number} alpha 0–1
 * @returns {string} the same colour with that alpha, as a CSS `rgba` string
 * @throws {TypeError} on anything that is not a 3- or 6-digit hex colour
 */
export function withAlpha(hex, alpha) {
  if (typeof hex !== 'string' || !/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(hex.trim())) {
    throw new TypeError(`withAlpha: not a hex colour: ${String(hex)}`);
  }
  let body = hex.trim().slice(1);
  if (body.length === 3) body = body.split('').map((c) => c + c).join('');
  const r = parseInt(body.slice(0, 2), 16);
  const g = parseInt(body.slice(2, 4), 16);
  const b = parseInt(body.slice(4, 6), 16);
  return `${RGBA}(${r},${g},${b},${alpha})`;
}

/**
 * The shared half of a simple category/value ECharts option.
 *
 * `orientation` decides which axis is which: `'vertical'` puts the categories on
 * x and the values on y, `'horizontal'` the other way round. The value axis of a
 * horizontal chart keeps the `boundaryGap: [0, 0.01]` the app's team/user bar
 * chart had, so its longest bar never touches the right edge.
 *
 * @param {Object} args
 * @param {{ fontColor: string }} args.theme a RESOLVED theme object — `useChartTheme().value`
 *   inside a component, `chartTheme(mode)` outside one
 * @param {'vertical'|'horizontal'} [args.orientation='vertical']
 * @param {string[]} args.categories the category-axis labels, in display order
 * @param {(value: number) => string} [args.formatValue] value-axis label formatter
 * @param {number} [args.labelRotate=0] category-axis label rotation
 * @param {string} [args.valueAxisName=''] value-axis title — already translated
 * @param {number|null} [args.valueMin] opt-in (AM_ABSORPTION_PLAN C4): the value axis' `min`
 * @param {number|null} [args.valueMax] opt-in: the value axis' `max` (e.g. 100 for a percentage)
 * @param {number|null} [args.valueMinInterval] opt-in: the value axis' `minInterval` (1 = whole
 *   numbers only, for counts)
 *   — each of the three is set ONLY when given (not null/undefined); without them the value axis
 *   is exactly what it always was (ECharts scales it)
 * @param {number|string|null} [args.labelInterval] opt-in (AM_ABSORPTION_PLAN D2): the category
 *   axis' label `interval` — `'auto'` lets ECharts thin the labels so they never overlap (a dozen
 *   months in a narrow card); not given = `0`, every label, as always
 * @param {number|null} [args.labelWidth] opt-in (AM_ABSORPTION_PLAN D3): the category labels' max
 *   width in px, truncated past it (`overflow: 'truncate'`); not given = no width, no truncation
 *   — both category opt-ins change the category axis ONLY when given
 * @returns {{ grid: Object, tooltip: Object, xAxis: Object, yAxis: Object }}
 */
export function axisChartOption({
  theme,
  orientation = 'vertical',
  categories,
  formatValue = (value) => String(value),
  labelRotate = 0,
  valueAxisName = '',
  valueMin = null,
  valueMax = null,
  valueMinInterval = null,
  labelInterval = null,
  labelWidth = null,
} = {}) {
  const horizontal = orientation === 'horizontal';
  const given = (value) => value !== null && value !== undefined;

  const categoryAxis = {
    type: 'category',
    data: categories ?? [],
    axisTick: { alignWithLabel: true },
    axisLabel: {
      color: theme?.fontColor,
      rotate: labelRotate,
      // Opt-in (asset-management): the caller's interval; the key stays where it always was
      interval: given(labelInterval) ? labelInterval : 0,
    },
  };
  // Opt-in (asset-management): long category names cut at a width instead of crowding the plot
  if (given(labelWidth)) {
    categoryAxis.axisLabel.width = labelWidth;
    categoryAxis.axisLabel.overflow = 'truncate';
  }

  const valueAxis = {
    type: 'value',
    name: valueAxisName,
    nameTextStyle: { color: theme?.fontColor },
    axisLabel: {
      color: theme?.fontColor,
      formatter: (value) => formatValue(value),
    },
  };
  // Horizontal bars run along the value axis; the gap keeps the longest one off
  // the edge (verbatim from TeamUserDocumentBarChart.vue).
  if (horizontal) valueAxis.boundaryGap = [0, 0.01];
  // Opt-in bounds (asset-management): a key only when the caller asked for it
  if (valueMin !== null && valueMin !== undefined) valueAxis.min = valueMin;
  if (valueMax !== null && valueMax !== undefined) valueAxis.max = valueMax;
  if (valueMinInterval !== null && valueMinInterval !== undefined) valueAxis.minInterval = valueMinInterval;

  return {
    grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
    // The caller adds `axisPointer` and `formatter` — those are per-chart.
    tooltip: {
      trigger: 'axis',
      confine: true,
      enterable: true,
      extraCssText: 'max-width: 320px; overflow-x: auto;',
    },
    xAxis: horizontal ? valueAxis : categoryAxis,
    yAxis: horizontal ? categoryAxis : valueAxis,
  };
}
